import { useState, useEffect } from 'react';
import { useToast } from '../../context/toast-context';
import { BranchService } from '../../services/branch-service';
import { useTranslation } from 'react-i18next';
import { BranchStyles } from '../../types';
import { Rating, styled } from '@mui/material';
import profileGenericPic from '../../assets/img/logos/profileGenericPic.jpg';
import DivixLogo from '../../assets/img/logos/divixNegro.png';
import { isValidImageFile } from '../../utils/helpers';

const StyledRating = styled(Rating)(({ theme }) => ({
  "& .MuiRating-iconFilled": {
    color: "#f3c433",
  },
  "& .MuiRating-iconHover": {
    color: "#f3c433",
  },
}));

interface SetupStylesFormData {
  textLandingColor: string;
  headerLandingColor: string;
  photoLogo?: File;
  branchId: number;
}

interface ColorSettingsProps {
  branchId: number;
  onClose: () => void;
  onlyRatings: boolean;
}

export const ColorSettings = ({ branchId, onClose, onlyRatings }: ColorSettingsProps) => {
  const [backgroundColor, setBackgroundColor] = useState('');
  const [textColor, setTextColor] = useState('');
  const [logo, setLogo] = useState<File | null>(null);
  const [currentLogoUrl, setCurrentLogoUrl] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { showToast } = useToast();
  const { t } = useTranslation();

  useEffect(() => {

    const branchService = new BranchService();
    branchService.getBranchSettings(
      { branchId },
      async (data) => {
        if (data && data.length > 0) {
          const settings = data[0] as unknown as BranchStyles;

          setTextColor(settings.textLandingColor || '#3a3a3a');
          setBackgroundColor(settings.headerLandingColor || '#e0e0e0');
          
          if (settings.photoLogo) {
            setCurrentLogoUrl(settings.photoLogo);
          }
         }
        
        setIsLoading(false);
      },
      (errorMessage: string) => {
        showToast(errorMessage, 'error');
        setIsLoading(false);
      }
    );
  }, [showToast, branchId]);

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!isValidImageFile(file)) {
        showToast(t("images.update-avatar.invalid-format"), "error");
        e.target.value = '';
        setLogo(null);
        return;
      }
      
      setLogo(file);
      setCurrentLogoUrl('');
    }
  };

  const handleSave = async () => {
    if (!branchId) {
      showToast('Error: No se pudo identificar la sucursal', 'error');
      return;
    }

    // Validar que el logo sea una imagen válida si existe
    if (logo && !isValidImageFile(logo)) {
      showToast(t("images.update-avatar.invalid-format"), "error");
      return;
    }

    setIsSaving(true);

    const branchService = new BranchService();

    try {
      const formData: SetupStylesFormData = {
        textLandingColor: textColor,
        headerLandingColor: backgroundColor,
        branchId,
      };

      if (logo) {
        formData.photoLogo = logo;
      }

      await branchService.setupStyles(
        formData,
        () => {
          if (!logo) {
            setCurrentLogoUrl(currentLogoUrl);
          }
          showToast('Configuración guardada exitosamente', 'success');
          onClose?.();
        },
        (errorMessage: string) => {
          showToast(errorMessage, 'error');
        }
      );
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="w-full flex justify-center items-center py-8">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 bg-[#EF0BB8] rounded-full animate-bounce [animation-delay:-0.3s]"></div>
          <div className="w-2 h-2 bg-[#EF0BB8] rounded-full animate-bounce [animation-delay:-0.15s]"></div>
          <div className="w-2 h-2 bg-[#EF0BB8] rounded-full animate-bounce"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center">
      <div className="flex w-full items-start">
        <form className="flex flex-col gap-5 max-w-xs w-full pr-4 mt-8">
          <div className="flex flex-col gap-1 w-full items-center">
            <span className="text-center w-full text-black font-montserrat font-normal text-base">{t("branches.settings.bg-color")}</span>
            <div className="flex items-center gap-4">
              <div className="w-12 h-8 relative">
                <input 
                  type="color" 
                  className="absolute inset-0 w-full h-full p-0 border border-[#838383] rounded cursor-pointer" 
                  value={backgroundColor || '#ffffff'}
                  onChange={(e) => setBackgroundColor(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-1 w-full items-center">
            <span className="text-center w-full text-black font-montserrat font-normal text-base">{t("branches.settings.text-color")}</span>
            <div className="flex items-center gap-4">
              <div className="w-12 h-8 relative">
                <input 
                  type="color" 
                  className="absolute inset-0 w-full h-full p-0 border border-[#838383] rounded cursor-pointer" 
                  value={textColor || '#000000'}
                  onChange={(e) => setTextColor(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-2 w-full items-center">
            <span className="text-center w-full text-black font-montserrat font-normal text-base">{t("branches.settings.logo")}</span>
            <div className="w-full overflow-x-auto">
              <input 
                type="file" 
                className="block w-full text-sm text-[#838383] font-montserrat"
                accept="image/*"
                onChange={handleLogoChange}
                style={{maxWidth: '100%'}}
              />
            </div>
            {logo ? (
              <img 
                src={URL.createObjectURL(logo)} 
                alt="Nuevo logo" 
                className="mt-2 max-h-20 object-contain"
              />
            ) : currentLogoUrl ? (
              <img 
                src={currentLogoUrl}
                alt="Logo actual" 
                className="mt-2 max-h-20 object-contain"
              />
            ) : null}
          </div>
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            className="mt-4 px-4 py-2 bg-[#EF0BB8] text-white rounded-lg hover:bg-[#d009a6] font-montserrat disabled:opacity-50"
          >
            {isSaving ? t("branches.settings.saving") : t("branches.settings.submit")}
          </button>
        </form>

        {/* Vista previa de la landing */}
        <div className="flex-1 border-l border-r border-b rounded-r-lg overflow-hidden shadow-lg max-w-[340px] ml-auto">
          <div className="h-[480px] w-full flex flex-col">
            <header
              className="pt-3"
              style={{
                backgroundColor: backgroundColor || "#e0e0e0",
              }}
            >
              <img
                className="mx-auto pb-2"
                style={{
                  maxWidth: '120px',
                  maxHeight: '60px',
                  width: 'auto',
                  height: 'auto',
                  objectFit: 'contain'
                }}
                src={logo ? URL.createObjectURL(logo) : (currentLogoUrl || DivixLogo)}
                alt="Logo"
              />
              <h3
                className="w-[260px] text-nowrap pb-3 mx-auto text-center font-light text-base"
                style={{
                  color: textColor || "#3a3a3a",
                }}
              >
                Califica la atención recibida:
              </h3>
            </header>
            <main className="bg-white flex flex-col justify-center items-center w-full relative mt-16">
              <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <div
                  className="w-16 h-16 rounded-full bg-gray-200 shadow-lg"
                  style={{
                    backgroundImage: `url(${profileGenericPic})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }}
                />
              </div>
              <div className="flex items-end text-black pt-10 mb-2 font-medium shadow-sm max-w-xs text-wrap">
                <div className="py-1 px-3 flex flex-col items-center bg-[#3a3a3a] rounded-full">
                  <span className="name text-sm text-center text-[#d6d6d6] leading-4">
                    Nombre del Empleado
                  </span>
                </div>
              </div>
              <div className="flex flex-col items-center justify-center">
                <StyledRating
                  name="preview-rating"
                  value={0}
                  size="medium"
                  sx={{ fontSize: "2.2rem" }}
                />
              </div>
              <div className="h-fit w-full max-w-xs pt-2 px-4 text-[#424242]">
                <textarea
                  className="w-full p-2 border rounded-lg text-sm"
                  placeholder="Escribe tu comentario aquí..."
                  rows={2}
                  disabled
                />
                <p className="text-xs text-center pt-1 text-[#3a3a3a] font-medium">
                  Los mensajes solo serán vistos por el manager del establecimiento
                </p>
              </div>
              <div className="pt-4 flex justify-between gap-2 px-4 items-center footer">
                <button
                  className="rounded-full py-1 w-40"
                  style={{
                    backgroundColor: backgroundColor || "#3b3b3b",
                    color: textColor || "#fff",
                  }}
                >
                  {/* onlyRatings ? "ENVIAR" : "SIN PROPINA" */}
                  ENVIAR
                </button>
                {/* {!onlyRatings && (
                  <button
                    className="rounded-full py-1 w-40"
                    style={{
                      backgroundColor: backgroundColor || "#f20bbb",
                      color: textColor || "#fff",
                    }}
                  >
                    DEJAR PROPINA
                  </button>
                )} */}
              </div>
            </main>
          </div>
        </div>
      </div>
    </div>
  );
};

